/**
 * Budget Table Filter Dialog Component
 *
 * Filter dialog for budget table view
 * Allows filtering by phase, date range, and value range
 *
 * Pattern from: bank-statement-filter-dialog.tsx
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { BudgetTableFilters } from "@/types/orcamentos.interface";

interface BudgetTableFilterDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Close callback */
  onClose: () => void;
  /** Current filters */
  filters: BudgetTableFilters;
  /** Apply filters callback */
  onApply: (filters: BudgetTableFilters) => void;
}

/**
 * Available phase options for filtering
 */
const phaseOptions = [
  { value: "Desenvolvimento do Projeto", label: "Desenvolvimento do Projeto" },
  { value: "Pré-Produção", label: "Pré-Produção" },
  { value: "Produção", label: "Produção" },
  { value: "Pós-Produção", label: "Pós-Produção" },
  { value: "Despesas Administrativas", label: "Despesas Administrativas" },
  { value: "Tributos e Taxas", label: "Tributos e Taxas" },
  { value: "Gerenciamento", label: "Gerenciamento" },
];

export function BudgetTableFilterDialog({
  open,
  onClose,
  filters,
  onApply,
}: BudgetTableFilterDialogProps) {
  const [localFilters, setLocalFilters] = useState<BudgetTableFilters>(filters);

  // Sync local state with prop changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, open]);

  /**
   * Toggle phase selection
   */
  const handlePhaseToggle = (phase: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      selectedPhases: prev.selectedPhases.includes(phase)
        ? prev.selectedPhases.filter((p) => p !== phase)
        : [...prev.selectedPhases, phase],
    }));
  };

  /**
   * Apply filters and close dialog
   */
  const handleApply = () => {
    onApply(localFilters);
  };

  /**
   * Clear all filters
   */
  const handleClear = () => {
    const clearedFilters: BudgetTableFilters = {
      selectedPhases: [],
      dateFrom: "",
      dateTo: "",
      valueMin: 0,
      valueMax: Infinity,
    };
    setLocalFilters(clearedFilters);
    onApply(clearedFilters);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#15203C] border-[#7846FF] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-semibold">
            Filtros
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Phase Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Fases</p>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {phaseOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`phase-${option.value}`}
                  checked={localFilters.selectedPhases.includes(option.value)}
                  onCheckedChange={() => handlePhaseToggle(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          {/* Date Range Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Período</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="dateFrom" className="text-xs text-gray-400 mb-1 block">
                  De
                </label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
              <div>
                <label htmlFor="dateTo" className="text-xs text-gray-400 mb-1 block">
                  Até
                </label>
                <Input
                  id="dateTo"
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
            </div>
          </div>

          {/* Value Range Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Valor</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="valueMin" className="text-xs text-gray-400 mb-1 block">
                  Mínimo
                </label>
                <Input
                  id="valueMin"
                  type="number"
                  placeholder="R$ 0"
                  value={localFilters.valueMin || ""}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      valueMin: Number(e.target.value) || 0,
                    }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
              <div>
                <label htmlFor="valueMax" className="text-xs text-gray-400 mb-1 block">
                  Máximo
                </label>
                <Input
                  id="valueMax"
                  type="number"
                  placeholder="R$ ∞"
                  value={
                    localFilters.valueMax === Infinity ? "" : localFilters.valueMax || ""
                  }
                  onChange={(e) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      valueMax: Number(e.target.value) || Infinity,
                    }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClear}
            className="flex-1"
          >
            Limpar
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
